// Pure branding resolver/patch-builder: field-level plant theme overrides, resolved independently
// against organization defaults and application defaults. No React, no redux, no fetch — every
// function here takes plain data in and returns plain data out, so the resolution rules (own-
// property presence, never `||`/truthiness) are easy to unit test in isolation.
import LynkIdlogo from "../LynkIdlogo.png";

export const LEAF_PATHS = ["theme.primaryColor", "theme.selectedTheme", "title", "logo", "favIcon", "statIcon.total", "statIcon.todayIn", "statIcon.todayOut", "statIcon.currentIn"];

export const ASSET_LEAVES = ["logo", "favIcon", "statIcon.total", "statIcon.todayIn", "statIcon.todayOut", "statIcon.currentIn"];

// `logo`/`favIcon` have a real application asset; the stat icons have none — their component
// supplies its own built-in inline icon when nothing overrides it (source stays "application").
export const APP_DEFAULTS = {
	theme: { primaryColor: "#ff7200", selectedTheme: "lightMode" },
	title: "LynkID",
	logo: LynkIdlogo,
	favIcon: "/favicon.ico",
	statIcon: { total: undefined, todayIn: undefined, todayOut: undefined, currentIn: undefined },
};

export function leafGet(obj, path) {
	return path.split(".").reduce((node, key) => (node && typeof node === "object" ? node[key] : undefined), obj);
}

export function leafHasOwn(obj, path) {
	const parts = path.split(".");
	let node = obj;
	for (const part of parts) {
		if (!node || typeof node !== "object" || !Object.prototype.hasOwnProperty.call(node, part)) return false;
		node = node[part];
	}
	return true;
}

export function normalizeColor(value) {
	if (typeof value !== "string") return undefined;
	const trimmed = value.trim();
	return /^#[0-9a-fA-F]{6}$/.test(trimmed) ? trimmed.toLowerCase() : undefined;
}

export function normalizeMode(value) {
	return value === "lightMode" || value === "darkMode" ? value : undefined;
}

// Same limit the backend enforces on title (users orgTheme / VMS plantTheme).
export const TITLE_MAX_LENGTH = 100;

export function normalizeTitle(value) {
	if (typeof value !== "string") return undefined;
	const trimmed = value.trim();
	return trimmed.length && trimmed.length <= TITLE_MAX_LENGTH ? trimmed : undefined;
}

const SCALAR_NORMALIZERS = {
	"theme.primaryColor": normalizeColor,
	"theme.selectedTheme": normalizeMode,
	title: normalizeTitle,
};

function resolveScalarLeaf(path, plantOverrides, orgDefaults) {
	const normalize = SCALAR_NORMALIZERS[path];
	if (leafHasOwn(plantOverrides, path)) {
		const value = normalize(leafGet(plantOverrides, path));
		if (value !== undefined) return { value, source: "plant" };
	}
	if (leafHasOwn(orgDefaults, path)) {
		const value = normalize(leafGet(orgDefaults, path));
		if (value !== undefined) return { value, source: "organization" };
	}
	return { value: leafGet(APP_DEFAULTS, path), source: "application" };
}

// Asset leaves always resolve to `{ value: {url,fileName,expiresAt} | undefined, source, hidden }`.
// `hidden: true` means an explicit plant/org null: render nothing, never fall through further.
// `value: undefined` at the application level means "the consuming component supplies its own
// built-in fallback" (used for the three stat icons, which have no application-level asset).
function resolveAssetLeaf(path, plantOverrides, orgDefaults, now) {
	if (leafHasOwn(plantOverrides, path)) {
		const raw = leafGet(plantOverrides, path);
		if (raw === null) return { value: null, source: "plant", hidden: true };
		if (raw && typeof raw === "object") return { value: Date.parse(raw.expiresAt) <= now ? undefined : raw, source: "plant", hidden: false };
	}
	if (leafHasOwn(orgDefaults, path)) {
		const raw = leafGet(orgDefaults, path);
		if (raw && typeof raw === "object") return { value: Date.parse(raw.expiresAt) <= now ? undefined : raw, source: "organization", hidden: false };
	}
	const appDefault = leafGet(APP_DEFAULTS, path);
	return { value: appDefault === undefined ? undefined : { url: appDefault }, source: "application", hidden: false };
}

/**
 * Resolves every in-scope leaf independently against `plantOverrides` (a plant's raw `overrides`
 * object from `plant/get-theme`) and `orgDefaults` (the organization's raw URL-mode themeSettings).
 * Both default to `{}` so "no plant selected" / "org defaults not loaded yet" degrade to
 * organization/application defaults rather than throwing.
 */
export function resolveBranding(plantOverrides = {}, orgDefaults = {}, now = Date.now()) {
	return {
		theme: {
			primaryColor: resolveScalarLeaf("theme.primaryColor", plantOverrides, orgDefaults),
			selectedTheme: resolveScalarLeaf("theme.selectedTheme", plantOverrides, orgDefaults),
		},
		title: resolveScalarLeaf("title", plantOverrides, orgDefaults),
		logo: resolveAssetLeaf("logo", plantOverrides, orgDefaults, now),
		favIcon: resolveAssetLeaf("favIcon", plantOverrides, orgDefaults, now),
		statIcon: {
			total: resolveAssetLeaf("statIcon.total", plantOverrides, orgDefaults, now),
			todayIn: resolveAssetLeaf("statIcon.todayIn", plantOverrides, orgDefaults, now),
			todayOut: resolveAssetLeaf("statIcon.todayOut", plantOverrides, orgDefaults, now),
			currentIn: resolveAssetLeaf("statIcon.currentIn", plantOverrides, orgDefaults, now),
		},
	};
}

// --- Editor patch building ---------------------------------------------------

// --- Asset file checks -------------------------------------------------------

// Mirrors the backend asset rules (users orgTheme / VMS plantTheme): PNG, JPEG, WebP or ICO,
// identified by magic bytes, at most 2 MiB. Checking here saves reading and sending a file the
// backend would reject anyway.
export const ASSET_ACCEPT = ".png, .jpg, .jpeg, .webp, .ico";
export const ASSET_MAX_BYTES = 2 * 1024 * 1024;

const IMAGE_SIGNATURES = [
	{ contentType: "image/png", detect: (b) => b.length >= 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47 && b[4] === 0x0d && b[5] === 0x0a && b[6] === 0x1a && b[7] === 0x0a },
	{ contentType: "image/jpeg", detect: (b) => b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff },
	{ contentType: "image/webp", detect: (b) => b.length >= 12 && String.fromCharCode(...b.slice(0, 4)) === "RIFF" && String.fromCharCode(...b.slice(8, 12)) === "WEBP" },
	{ contentType: "image/x-icon", detect: (b) => b.length >= 4 && b[0] === 0x00 && b[1] === 0x00 && b[2] === 0x01 && b[3] === 0x00 },
];

// Carries a message that is safe to show the user as-is.
export class AssetFileError extends Error {}

function formatBytes(bytes) {
	if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, "")} MB`;
	return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function bytesToBase64(bytes) {
	let binary = "";
	for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
	return btoa(binary);
}

/**
 * Rejects with an AssetFileError when the file is empty, over `maxBytes`, or not a PNG, JPEG,
 * WebP or ICO by its bytes; resolves with the detected content type otherwise. The detected type
 * is what callers should send: `file.type` can be empty (common for .ico) or wrong for a
 * renamed file, and the backend requires the declared type to match the bytes.
 */
export async function checkImageFile(file, { maxBytes = ASSET_MAX_BYTES, maxLabel = formatBytes(maxBytes) } = {}) {
	if (!file || !file.size) throw new AssetFileError("The selected file is empty.");
	if (file.size > maxBytes) {
		throw new AssetFileError(`Image must be ${maxLabel} or smaller (selected file is ${formatBytes(file.size)}).`);
	}
	const header = new Uint8Array(await file.slice(0, 12).arrayBuffer());
	const match = IMAGE_SIGNATURES.find((sig) => sig.detect(header));
	if (!match) throw new AssetFileError("File is not a valid PNG, JPEG, WebP or ICO image.");
	return match.contentType;
}

/**
 * Checks the file (see checkImageFile), then reads it as the raw-base64 upload descriptor the
 * backend contract expects (`{fileName, contentType, base64}` with NO `data:...;base64,` prefix).
 */
export async function fileToUploadDescriptor(file) {
	const contentType = await checkImageFile(file);
	return {
		fileName: file.name,
		contentType,
		base64: bytesToBase64(new Uint8Array(await file.arrayBuffer())),
	};
}

/**
 * Builds the `{set, unset}` request body from a caller-tracked map of explicit pending operations
 * (never a diff of the displayed/merged object — untouched inherited fields must never enter
 * `set`, and reverting an edit back to its original value/source must remove its entry from
 * `pendingOps` before this is called).
 *
 * `pendingOps`: Map<leafPath, { op: "set", value } | { op: "unset" } | { op: "hide" }>
 * (`hide` is asset-only sugar for `{op:"set", value:null}`.)
 */
export function buildThemePatch(pendingOps) {
	const set = {};
	const unset = [];
	const entries = pendingOps instanceof Map ? pendingOps.entries() : Object.entries(pendingOps);
	for (const [path, entry] of entries) {
		if (!entry) continue;
		if (entry.op === "unset") {
			unset.push(path);
		} else if (entry.op === "hide") {
			set[path] = null;
		} else if (entry.op === "set") {
			set[path] = entry.value;
		}
	}
	return { set, unset };
}

export function isAssetLeaf(path) {
	return ASSET_LEAVES.includes(path);
}

// Which editor scopes may hide an asset (store an explicit `null`). Only a plant can hide an
// inherited image; at organization level `null` is rejected by the server — there is nothing
// above it to hide, and "unset" already means "fall back to the application default".
export function canHideInScope(scope) {
	return scope === "plant";
}
